/** The continuous-voice conversation loop, shared by the exam speaking
 * partner and the standalone speaking bot.
 *
 * Start ONCE → the partner greets aloud (browser TTS) → the microphone
 * arms itself (browser speech recognition) → each finished utterance goes
 * to the server → the reply is spoken → the mic re-arms. No per-turn
 * button; it runs until the learner ends it (or the server says ended).
 * Voice never leaves the browser — only TEXT turns are sent.
 */
import { useEffect, useRef, useState } from "react";
import * as api from "../../api/exams";
import { ApiError } from "../../api/http";
import { createRecognizer, sttAvailable, type Recognizer } from "../../utils/stt";
import { speak } from "../../utils/tts";

export interface VoiceConversation {
  session: { id: string; maxTurns: number | null } | null;
  messages: api.SpeakingTurn[];
  turns: number;
  busy: boolean;
  listening: boolean;
  error: string | null;
  ended: api.SpeakingSession | null;
  start: () => Promise<void>;
  sendText: (text: string) => void;
  end: () => Promise<void>;
  reset: () => void;
  showEnded: (session: api.SpeakingSession) => void;
}

export function useVoiceConversation(opts: {
  /** Opens the session server-side (exam item, or a free chat). */
  startSession: () => Promise<api.SpeakingStartResult>;
  /** Speech-recognition locale, e.g. "de-DE". */
  lang?: string;
  /** Fired when a conversation finishes (e.g. to refresh history). */
  onFinished?: () => void;
}): VoiceConversation {
  const { startSession, lang, onFinished } = opts;
  const [session, setSession] = useState<{ id: string; maxTurns: number | null } | null>(null);
  const [messages, setMessages] = useState<api.SpeakingTurn[]>([]);
  const [turns, setTurns] = useState(0);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState<api.SpeakingSession | null>(null);

  const recognizerRef = useRef<Recognizer | null>(null);
  const liveRef = useRef(false);
  const busyRef = useRef(false);
  const sessionRef = useRef<string | null>(null);

  // Always stop the mic when the surface unmounts.
  useEffect(
    () => () => {
      liveRef.current = false;
      recognizerRef.current?.stop();
    },
    [],
  );

  const startListening = () => {
    if (!liveRef.current || !recognizerRef.current) return;
    setListening(true);
    recognizerRef.current.start();
  };

  const speakThenListen = (text: string) => {
    const spoke = speak(text, { lang, onEnd: () => startListening() });
    if (!spoke) startListening();
  };

  const finish = (row: api.SpeakingSession) => {
    liveRef.current = false;
    recognizerRef.current?.stop();
    setListening(false);
    setEnded(row);
    onFinished?.();
  };

  const handleUtterance = async (text: string) => {
    const sessionId = sessionRef.current;
    if (!sessionId || busyRef.current) return;
    recognizerRef.current?.stop();
    setListening(false);
    setMessages((m) => [...m, { role: "learner", text }]);
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const r = await api.replySpeaking(sessionId, text);
      setMessages((m) => [...m, { role: "assistant", text: r.reply }]);
      setTurns(r.turns);
      if (r.ended) finish(r);
      else speakThenListen(r.reply);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await startSession();
      sessionRef.current = r.id;
      liveRef.current = true;
      setSession({ id: r.id, maxTurns: r.max_turns });
      setMessages([{ role: "assistant", text: r.reply }]);
      setTurns(0);
      setEnded(null);
      if (sttAvailable()) {
        recognizerRef.current = createRecognizer({
          lang,
          onFinal: (text) => {
            void handleUtterance(text);
          },
          onEnd: () => setListening(false),
        });
      }
      speakThenListen(r.reply);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    const sessionId = sessionRef.current;
    if (!sessionId) return;
    recognizerRef.current?.stop();
    setListening(false);
    setBusy(true);
    setError(null);
    try {
      finish(await api.endSpeaking(sessionId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setBusy(false);
    }
  };

  const sendText = (text: string) => {
    const cleaned = text.trim();
    if (cleaned) void handleUtterance(cleaned);
  };

  const reset = () => {
    sessionRef.current = null;
    liveRef.current = false;
    recognizerRef.current?.stop();
    setSession(null);
    setMessages([]);
    setTurns(0);
    setEnded(null);
    setError(null);
  };

  return {
    session, messages, turns, busy, listening, error, ended,
    start, sendText, end, reset, showEnded: setEnded,
  };
}
